import {
    FloorPattern,
    ProductInstallation,
    MosaicShape,
    OS,
    ProductCombinedImage,
    ProductCustomField,
    ProductExtendedDto,
    ProductPlacementOptions,
    ProductStructure,
    ProductTileSize,
    ProductTechnicalParameters,
    AssetModel3DDto,
} from '@floori/models';
import { assetImageMapper } from '@floori-web/utils/pipes';
import { DynamicProduct } from '@floori-web/models';
import {
    AssetImageDto,
    DimensionUnit,
    FieldTranslation,
    Label,
    ProductRGBAColor,
    ProductType,
} from '@floori-web-components';
import { Product3DModelPropertiesDto } from './product-3d-model-properties.dto';

export class ProductExtended {
    readonly id: string;
    readonly type?: ProductType;
    readonly sku?: string;
    readonly ean?: string;
    readonly externalId?: string;
    readonly catalogId: string;
    readonly manufacturer?: string;
    readonly supplier?: string;
    readonly placementOptions: ProductPlacementOptions[];
    readonly patterns?: FloorPattern[];
    readonly name?: FieldTranslation;
    readonly description?: FieldTranslation;
    readonly isGlossy?: boolean;
    readonly model3D?: AssetModel3DDto;
    readonly gltfUrl?: string;
    readonly usdzUrl?: string;
    readonly showDimensions?: boolean;
    readonly groutColor?: string;
    readonly groutWidth?: number;
    readonly groutUnit?: DimensionUnit;
    readonly custom?: ProductCustomField;
    readonly orderSampleUrl?: string;
    readonly roomTypes?: string[];
    readonly stockQuantity?: number;
    readonly mosaicShape?: MosaicShape;
    readonly metadata?: Record<string, unknown>;
    readonly productStructure?: ProductStructure;
    readonly installation?: ProductInstallation;
    readonly technicalParameters?: ProductTechnicalParameters;
    readonly labels?: Label[];
    readonly model3DProperties?: Product3DModelPropertiesDto;
    combined: ProductCombinedImage[] = [];
    image?: AssetImageDto | null;
    rgbaColor?: ProductRGBAColor | null;
    groupedTileOptions?: ProductTileSize[];
    tileOptions: ProductTileSize[] = [];
    width: number;
    height: number;
    countX?: number;
    countY?: number;
    defaultPattern?: FloorPattern;
    selectedSize?: ProductTileSize;

    get isFloor(): boolean {
        return !this.isRug && !this.is3DModel;
    }

    get isRug(): boolean {
        return this.type === ProductType.rug;
    }

    get is3DModel(): boolean {
        return this.type === ProductType.model3d || !!this.get3DModel()?.gltf;
    }

    get isEpoxy(): boolean {
        return (
            this.type === ProductType.epoxyBase ||
            this.type === ProductType.epoxyChips ||
            this.type === ProductType.epoxyCombined
        );
    }

    get hasValidCombinedImages(): boolean {
        return this.validateCombinedImages();
    }

    constructor(productExtendedDto: Partial<ProductExtendedDto> | ProductExtended) {
        Object.assign(this, { ...productExtendedDto });
        this.type = productExtendedDto?.type;
        this.id = productExtendedDto?.id || '';
        this.width = productExtendedDto?.width || 0;
        this.height = productExtendedDto?.height || 0;
        this.placementOptions = [...(productExtendedDto?.placementOptions || [])];
        if (!!productExtendedDto?.image) {
            this.image = assetImageMapper(productExtendedDto?.image);
        }
        this.catalogId = productExtendedDto?.catalogId || '';
    }

    static fromDynamicProduct(product: DynamicProduct | null): ProductExtended | undefined {
        if (!product) {
            return;
        }

        return new ProductExtended(product);
    }

    updateModel(partial: Partial<ProductExtended>): ProductExtended {
        Object.assign(this, partial);
        return this;
    }

    generateTileOptions(): ProductExtended {
        this.tileOptions = this.tileOptions
            .filter((tileOption, index) => this.combined[index])
            .map(tileOption => this.convertTileToMeters(tileOption));
        this.combined = this.combined
            ? (this.combined as Array<any>).filter(combined => combined)
            : [];
        this.groupedTileOptions = this.groupByShorterDimension(this.tileOptions);
        return this;
    }

    hasSupportedModel3D(os: OS, variantIndex: number): boolean {
        const tileOption = this.tileOptions?.[variantIndex];
        return (
            (os === OS.ios && !!tileOption?.model3D?.usdz) ||
            (os !== OS.ios && !!tileOption?.model3D?.gltf)
        );
    }

    findTileGroupIndex(selectedIndex: number): number {
        let selectedTile = this.tileOptions[selectedIndex];

        if (!selectedTile && this.tileOptions.length) {
            selectedTile = this.tileOptions[0];
        }

        const tileGroupIndex = (this.groupedTileOptions || []).findIndex(
            element => !!element?.id && element?.id === selectedTile?.id,
        );

        return tileGroupIndex !== -1 ? tileGroupIndex : 0;
    }

    getPreferredPattern(pattern?: FloorPattern): FloorPattern {
        const defaultPattern = this.defaultPattern || this.patterns?.[0] || FloorPattern.random;
        return pattern && this.patterns?.includes(pattern) ? pattern : defaultPattern;
    }

    get3DModel(): AssetModel3DDto | undefined {
        if (this.model3D?.gltf?.length) {
            return this.model3D;
        }

        return this.selectedSize?.model3D;
    }

    private validateCombinedImages(): boolean {
        if (this.rgbaColor) {
            return true;
        }

        if (!this.combined || this.combined.length === 0) {
            return false;
        }

        return this.combined.some(
            combined => combined?.image && (combined.image.large || combined.image.medium),
        );
    }

    private groupByShorterDimension(tileOptions: ProductTileSize[]): ProductTileSize[] {
        const groupBy = (
            list: ProductTileSize[],
            keyGetter: (keyGetter: ProductTileSize) => number,
        ): ProductTileSize[] => {
            const localMap = new Map();
            list.forEach(item => {
                const key = keyGetter(item);
                const collection = localMap.get(key);
                if (!collection) {
                    localMap.set(key, [item]);
                } else {
                    collection.push(item);
                }
            });
            return Array.from(localMap).map(value => value[1]);
        };
        const horizontal = tileOptions.filter(el => el.width >= el.height);
        const vertical = tileOptions.filter(el => el.width < el.height);

        const horizontalGroup = groupBy(horizontal, tileOption => tileOption.height);
        const verticalGroup = groupBy(vertical, tileOption => tileOption.width);

        return horizontalGroup.concat(verticalGroup);
    }

    private convertTileToMeters(tileOption: ProductTileSize): ProductTileSize {
        if (tileOption.originalWidth || tileOption.originalHeight) {
            return tileOption;
        }

        let scaleFactor = 1;
        const newTileOptions = structuredClone(tileOption);
        newTileOptions.originalUnit = tileOption.unit;
        newTileOptions.originalWidth = tileOption.width;
        newTileOptions.originalHeight = tileOption.height;
        switch (newTileOptions.unit) {
            case DimensionUnit.inch:
                scaleFactor = 0.0254;
                break;
            case DimensionUnit.mm:
                scaleFactor = 0.001;
                break;
        }
        // todo why fraction is 4 where in other places is 2?
        newTileOptions.width = Number.parseFloat((newTileOptions.width * scaleFactor).toFixed(4));
        newTileOptions.height = Number.parseFloat((newTileOptions.height * scaleFactor).toFixed(4));
        newTileOptions.unit = DimensionUnit.meter;
        newTileOptions.groutWidth = Number.parseFloat(
            (newTileOptions.groutWidth * scaleFactor).toFixed(4),
        );
        return newTileOptions;
    }
}
